import React from 'react';
import {
  Box,
  Stack,
  Typography,
  Button,
  Chip,
} from '@mui/material';
import {
  Layers,
  Users,
  Eye,
  Trash2,
  Share2,
} from 'lucide-react';
import { QueueSelectDropdown } from './QueueSelectDropdown.jsx';

export const QueueHeader = ({
  queueName = '',
  availableQueues = [],
  onSelectQueue,
  vhost = '/',
  state = 'running',
  consumerCount = 0,
  bindingCount = 0,
  onPeekMessages = () => {},
  onViewConsumers = () => {},
  onViewBindings = () => {},
  onPurgeQueue = () => {},
}) => {
  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      justifyContent="space-between"
      alignItems={{ xs: 'flex-start', md: 'center' }}
      spacing={2.5}
      sx={{
        pb: 2.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={2}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 1,
            bgcolor: 'rgba(0, 105, 81, 0.1)',
            color: '#006951',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Layers style={{ width: 22, height: 22 }} />
        </Box>

        <Box>
          <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Queue
            </Typography>

            {availableQueues.length > 0 && onSelectQueue ? (
              <QueueSelectDropdown
                selectedQueue={queueName}
                availableQueues={availableQueues}
                onSelectQueue={onSelectQueue}
                size="md"
              />
            ) : (
              <Typography
                variant="h6"
                sx={{
                  fontFamily: 'monospace',
                  fontWeight: 600,
                  color: '#006951',
                }}
              >
                {queueName}
              </Typography>
            )}

            <Chip
              label={state || 'running'}
              size="small"
              sx={{
                height: 22,
                fontSize: '0.7rem',
                fontWeight: 700,
                bgcolor: 'rgba(16, 185, 129, 0.12)',
                color: '#059669',
              }}
            />
          </Stack>

          <Stack direction="row" spacing={1.5} alignItems="center" mt={0.5}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Virtual host: <strong>{decodeURIComponent(vhost || '/')}</strong>
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>•</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Consumers: <strong>{consumerCount}</strong>
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>•</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Bindings: <strong>{bindingCount}</strong>
            </Typography>
          </Stack>
        </Box>
      </Stack>

      {/* Action Buttons */}
      <Stack direction="row" spacing={1} flexWrap="wrap">
        <Button
          size="small"
          variant="outlined"
          startIcon={<Eye style={{ width: 14, height: 14, color: '#006951' }} />}
          onClick={onPeekMessages}
          sx={{
            borderColor: 'divider',
            color: 'text.primary',
            fontSize: '0.75rem',
            '&:hover': {
              borderColor: '#006951',
              bgcolor: 'rgba(0, 105, 81, 0.04)',
            },
          }}
        >
          Get Messages
        </Button>

        <Button
          size="small"
          variant="outlined"
          startIcon={<Users style={{ width: 14, height: 14, color: '#0284c7' }} />}
          onClick={onViewConsumers}
          sx={{
            borderColor: 'divider',
            color: 'text.primary',
            fontSize: '0.75rem',
            '&:hover': {
              borderColor: '#0284c7',
              bgcolor: 'rgba(2, 132, 199, 0.04)',
            },
          }}
        >
          Consumers ({consumerCount})
        </Button>

        <Button
          size="small"
          variant="outlined"
          startIcon={<Share2 style={{ width: 14, height: 14, color: '#7c3aed' }} />}
          onClick={onViewBindings}
          sx={{
            borderColor: 'divider',
            color: 'text.primary',
            fontSize: '0.75rem',
            '&:hover': {
              borderColor: '#7c3aed',
              bgcolor: 'rgba(124, 58, 237, 0.04)',
            },
          }}
        >
          Bindings ({bindingCount})
        </Button>

        <Button
          size="small"
          variant="outlined"
          color="error"
          startIcon={<Trash2 style={{ width: 14, height: 14 }} />}
          onClick={onPurgeQueue}
          sx={{
            fontSize: '0.75rem',
            bgcolor: 'rgba(239, 68, 68, 0.04)',
            borderColor: 'rgba(239, 68, 68, 0.3)',
            '&:hover': {
              bgcolor: 'rgba(239, 68, 68, 0.08)',
              borderColor: '#ef4444',
            },
          }}
        >
          Purge
        </Button>
      </Stack>
    </Stack>
  );
};
